// My Shopstop V1086 — Windows-style product document window.
// The native browser PDF reader retains responsibility for PDF zoom and paging.
(function shopstopProductPDFViewer() {
  if (window.ShopstopProductPDFViewer) return;

  const linkSelector = '[data-ss-product-documents-panel] .ss-product-document-link';
  const viewerSelector = '[data-ss-pdf-viewer]';
  const viewportMargin = 8;
  const minWindowWidth = 420;
  const minWindowHeight = 260;
  const savedGeometry = new WeakMap();
  let activePointer = null;

  function clamp(number, low, high) {
    return Math.min(Math.max(number, low), high);
  }

  function currentBounds() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    return {
      maxWidth: Math.max(1, width - viewportMargin * 2),
      maxHeight: Math.max(1, height - viewportMargin * 2),
      width: width,
      height: height
    };
  }

  function normalizeGeometry(geometry) {
    const bounds = currentBounds();
    const width = clamp(geometry.width, Math.min(minWindowWidth, bounds.maxWidth), bounds.maxWidth);
    const height = clamp(geometry.height, Math.min(minWindowHeight, bounds.maxHeight), bounds.maxHeight);
    return {
      left: clamp(geometry.left, viewportMargin, Math.max(viewportMargin, bounds.width - width - viewportMargin)),
      top: clamp(geometry.top, viewportMargin, Math.max(viewportMargin, bounds.height - height - viewportMargin)),
      width: width,
      height: height
    };
  }

  function applyGeometry(viewer, geometry) {
    const position = normalizeGeometry(geometry);
    viewer.style.inset = 'auto';
    viewer.style.left = `${position.left}px`;
    viewer.style.top = `${position.top}px`;
    viewer.style.width = `${position.width}px`;
    viewer.style.height = `${position.height}px`;
    viewer.style.margin = '0';
    savedGeometry.set(viewer, position);
  }

  function clearGeometryStyles(viewer) {
    for (const property of ['inset', 'top', 'right', 'bottom', 'left', 'width', 'height', 'margin']) {
      viewer.style.removeProperty(property);
    }
  }

  function restoreGeometry(viewer) {
    // A desktop window's saved dimensions must not override the mobile layout.
    if (window.matchMedia('(max-width: 600px)').matches) {
      clearGeometryStyles(viewer);
      return;
    }
    const geometry = savedGeometry.get(viewer);
    if (geometry) applyGeometry(viewer, geometry);
    else clearGeometryStyles(viewer);
  }

  function updateWindowControls(viewer) {
    const toggle = viewer.querySelector('[data-ss-pdf-viewer-toggle-size]');
    if (!toggle) return;
    const minimized = viewer.classList.contains('is-minimized');
    const maximized = viewer.classList.contains('is-maximized');
    const label = minimized ? 'Wiederherstellen' : (maximized ? 'Größe wiederherstellen' : 'Maximieren');
    toggle.setAttribute('aria-label', label);
    toggle.setAttribute('title', label);
    toggle.setAttribute('aria-pressed', String(maximized && !minimized));
  }

  function stopPointerOperation() {
    if (!activePointer) return;
    const operation = activePointer;
    activePointer = null;
    operation.viewer.classList.remove('is-interacting');
    if (operation.handle.hasPointerCapture?.(operation.pointerId)) {
      operation.handle.releasePointerCapture(operation.pointerId);
    }
  }

  function minimizeWindow(viewer) {
    if (!viewer.open || viewer.classList.contains('is-minimized')) return;
    stopPointerOperation();
    // A minimized window is nonmodal: the customer can use the product page.
    viewer.close();
    viewer.classList.add('is-minimized');
    clearGeometryStyles(viewer);
    viewer.show();
    updateWindowControls(viewer);
  }

  function toggleWindowSize(viewer) {
    if (!viewer.open) return;
    stopPointerOperation();
    if (viewer.classList.contains('is-minimized')) {
      viewer.close();
      viewer.classList.remove('is-minimized');
      viewer.showModal();
      if (!viewer.classList.contains('is-maximized')) restoreGeometry(viewer);
    } else if (viewer.classList.contains('is-maximized')) {
      viewer.classList.remove('is-maximized');
      restoreGeometry(viewer);
    } else {
      viewer.classList.add('is-maximized');
      clearGeometryStyles(viewer);
    }
    updateWindowControls(viewer);
  }

  function closeWindow(viewer) {
    if (!viewer.open) return;
    stopPointerOperation();
    viewer.close();
    viewer.classList.remove('is-minimized', 'is-maximized');
    clearGeometryStyles(viewer);
    updateWindowControls(viewer);
  }

  function startPointerOperation(event) {
    if (activePointer || event.button !== 0 || !event.isPrimary || !(event.target instanceof Element)) return;
    // On small touch-oriented layouts, preserve the existing responsive dialog.
    if (window.matchMedia('(max-width: 600px)').matches) return;

    const handle = event.target.closest('[data-ss-pdf-resize]');
    const header = event.target.closest('[data-ss-pdf-viewer-drag]');
    if (!handle && !header) return;
    if (!handle && event.target.closest('button, a, input, select, textarea, [role="group"]')) return;

    const viewer = (handle || header).closest(viewerSelector);
    if (!viewer || !viewer.open || viewer.classList.contains('is-minimized') || viewer.classList.contains('is-maximized')) return;

    const rect = viewer.getBoundingClientRect();
    const direction = handle ? handle.getAttribute('data-ss-pdf-resize') : null;
    activePointer = {
      viewer: viewer,
      handle: handle || header,
      pointerId: event.pointerId,
      direction: direction,
      startX: event.clientX,
      startY: event.clientY,
      rect: { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height }
    };
    viewer.classList.add('is-interacting');
    event.preventDefault();
    try {
      activePointer.handle.setPointerCapture(event.pointerId);
    } catch (error) {
      // Document-level pointer listeners still handle this interaction.
    }
  }

  function movePointer(event) {
    const operation = activePointer;
    if (!operation || operation.pointerId !== event.pointerId) return;
    event.preventDefault();

    const dx = event.clientX - operation.startX;
    const dy = event.clientY - operation.startY;
    const rect = operation.rect;
    const bounds = currentBounds();

    if (!operation.direction) {
      applyGeometry(operation.viewer, {
        left: rect.left + dx,
        top: rect.top + dy,
        width: rect.width,
        height: rect.height
      });
      return;
    }

    let left = rect.left;
    let right = rect.right;
    let top = rect.top;
    let bottom = rect.bottom;
    const direction = operation.direction;
    const minimumWidth = Math.min(minWindowWidth, bounds.maxWidth);
    const minimumHeight = Math.min(minWindowHeight, bounds.maxHeight);

    if (direction.includes('w')) left = clamp(rect.left + dx, viewportMargin, right - minimumWidth);
    if (direction.includes('e')) right = clamp(rect.right + dx, left + minimumWidth, bounds.width - viewportMargin);
    if (direction.includes('n')) top = clamp(rect.top + dy, viewportMargin, bottom - minimumHeight);
    if (direction.includes('s')) bottom = clamp(rect.bottom + dy, top + minimumHeight, bounds.height - viewportMargin);

    applyGeometry(operation.viewer, { left: left, top: top, width: right - left, height: bottom - top });
  }

  function openDocument(link) {
    const section = link.closest('[id^="shopify-section-"]');
    const viewer = section && section.querySelector(viewerSelector);
    if (!viewer || typeof viewer.showModal !== 'function') return false;

    let source;
    try {
      source = new URL(link.getAttribute('href'), window.location.href);
    } catch (error) {
      return false;
    }
    if (source.protocol !== 'https:' && source.protocol !== 'http:') return false;

    const name = (link.querySelector('.ss-product-document-name')?.textContent || 'PDF-Dokument').trim();
    const title = viewer.querySelector('[data-ss-pdf-viewer-title]');
    const frame = viewer.querySelector('[data-ss-pdf-viewer-frame]');
    if (!title || !frame) return false;

    stopPointerOperation();
    if (viewer.open) viewer.close();
    viewer.classList.remove('is-minimized');
    title.textContent = name || 'PDF-Dokument';
    frame.title = name || 'PDF-Dokument';
    if (!viewer.classList.contains('is-maximized')) restoreGeometry(viewer);
    else clearGeometryStyles(viewer);
    updateWindowControls(viewer);
    viewer.showModal();
    frame.src = source.href;
    return true;
  }

  document.addEventListener('pointerdown', startPointerOperation);
  document.addEventListener('pointermove', movePointer);
  document.addEventListener('pointerup', function (event) {
    if (activePointer && event.pointerId === activePointer.pointerId) stopPointerOperation();
  });
  document.addEventListener('pointercancel', function (event) {
    if (activePointer && event.pointerId === activePointer.pointerId) stopPointerOperation();
  });
  window.addEventListener('blur', stopPointerOperation);
  window.addEventListener('resize', function () {
    document.querySelectorAll(`${viewerSelector}[open]`).forEach(function (viewer) {
      if (!viewer.classList.contains('is-minimized') && !viewer.classList.contains('is-maximized')) restoreGeometry(viewer);
    });
  });

  document.addEventListener('dblclick', function (event) {
    if (!(event.target instanceof Element) || event.target.closest('button, a, [role="group"]')) return;
    const header = event.target.closest('[data-ss-pdf-viewer-drag]');
    const viewer = header && header.closest(viewerSelector);
    if (viewer) toggleWindowSize(viewer);
  });

  document.addEventListener('click', function (event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const control = target.closest('[data-ss-pdf-viewer-minimize], [data-ss-pdf-viewer-toggle-size], [data-ss-pdf-viewer-close]');
    if (control) {
      const viewer = control.closest(viewerSelector);
      if (!viewer) return;
      if (control.hasAttribute('data-ss-pdf-viewer-minimize')) minimizeWindow(viewer);
      if (control.hasAttribute('data-ss-pdf-viewer-toggle-size')) toggleWindowSize(viewer);
      if (control.hasAttribute('data-ss-pdf-viewer-close')) closeWindow(viewer);
      return;
    }

    // Preserve v1085 behavior for direct PDF links (including modified clicks).
    if (event.defaultPrevented || event.button !== 0) return;
    const link = target.closest(linkSelector);
    if (link && openDocument(link)) event.preventDefault();
  });

  document.addEventListener('auxclick', function (event) {
    if (event.button !== 1 || event.defaultPrevented || !(event.target instanceof Element)) return;
    const link = event.target.closest(linkSelector);
    if (link && openDocument(link)) event.preventDefault();
  });

  document.addEventListener('close', function (event) {
    const viewer = event.target;
    if (!(viewer instanceof Element) || !viewer.matches(viewerSelector)) return;
    // Modal/nonmodal transitions also use close(); never unload an open PDF.
    if (viewer.open) return;
    if (activePointer && activePointer.viewer === viewer) stopPointerOperation();
    const frame = viewer.querySelector('[data-ss-pdf-viewer-frame]');
    if (frame) frame.removeAttribute('src');
    viewer.classList.remove('is-minimized', 'is-maximized');
    clearGeometryStyles(viewer);
    updateWindowControls(viewer);
  }, true);

  window.ShopstopProductPDFViewer = Object.freeze({ openDocument: openDocument });
})();
